import { useState } from 'react';
import { color } from '../styles/theme';
import { dateTime } from '../lib/format';
import {
  useSessions,
  useSignOutEverywhereElse,
  useChangePassword,
  passwordProblems,
  problemsFrom,
} from '../api/account';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  PageTitle,
  Card,
  CardTitle,
  Field,
  Button,
  Loading,
  ErrorNote,
  Tile,
  inputStyle,
} from '../components/ui';
import { PasswordChecklist } from '../components/AuthShell';

export function Account() {
  const { admin, signOut } = useAuth();
  const sessions = useSessions();
  const signOutOthers = useSignOutEverywhereElse();
  const change = useChangePassword();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * The rules the API said this password broke, held against the password it
   * refused: the checklist marks them until the password is changed. The
   * checks here mirror the API's, so this is for when the two disagree.
   */
  const [refused, setRefused] = useState<{ password: string; problems: string[] } | null>(null);

  const serverProblems = refused?.password === next ? refused.problems : [];
  const local = passwordProblems(next);
  const failing = [...new Set([...local, ...serverProblems])];
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready =
    current.length > 0 && next.length > 0 && failing.length === 0 && !mismatch && confirm.length > 0;

  async function submit() {
    setError(null);
    setRefused(null);
    try {
      const res = await change.mutateAsync({ currentPassword: current, newPassword: next });
      // Changing a password revokes every session, this one included. Signing
      // out is the honest response — the alternative is a dashboard on screen
      // whose next request 401s.
      if (res.reauthRequired) await signOut();
    } catch (err) {
      // A 422 names each broken rule: the API's sentence, then the rules, and
      // the same rules marked on the checklist above.
      const problems = problemsFrom(err);
      if (problems.length > 0) setRefused({ password: next, problems });
      const sentence = err instanceof ApiError ? err.message : 'Could not change your password.';
      setError(problems.length > 0 ? `${sentence} ${problems.join('. ')}.` : sentence);
    }
  }

  const rows = sessions.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <PageTitle sub="Your own sign-in details and the devices signed in as you.">
        Your account
      </PageTitle>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card pad={24}>
          <CardTitle style={{ marginBottom: 4 }}>Change your password</CardTitle>
          <p style={{ fontSize: 12.5, color: color.muted, margin: '0 0 14px' }}>
            Changing it signs you out on every device, including this one.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Current password">
              <input
                type="password"
                value={current}
                autoComplete="current-password"
                onChange={(e) => setCurrent(e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="New password">
              <input
                type="password"
                value={next}
                autoComplete="new-password"
                onChange={(e) => setNext(e.target.value)}
                style={inputStyle}
              />
            </Field>
            <PasswordChecklist problems={failing} dirty={next.length > 0} />
            <Field label="Confirm new password">
              <input
                type="password"
                value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)}
                style={inputStyle}
              />
            </Field>

            {mismatch && (
              <p style={{ color: color.red, fontSize: 13, fontWeight: 600, margin: 0 }}>
                Those two passwords do not match.
              </p>
            )}
            {error && <ErrorNote message={error} />}

            <Button
              onClick={() => void submit()}
              disabled={!ready || change.isPending}
              style={{ alignSelf: 'flex-start' }}
            >
              {change.isPending ? 'Changing…' : 'Change password'}
            </Button>
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Tile label="Signed in as" value={admin?.name ?? '—'} />
            <Tile label="Role" value={admin?.roleLabel ?? '—'} />
          </div>

          <Card pad={24}>
            <CardTitle style={{ marginBottom: 4 }}>Where you are signed in</CardTitle>
            <p style={{ fontSize: 12.5, color: color.muted, margin: '0 0 14px' }}>
              One row per device holding a live session.
            </p>

            {sessions.isLoading ? (
              <Loading />
            ) : sessions.error ? (
              <ErrorNote message={(sessions.error as Error).message} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {rows.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'baseline',
                      padding: '10px 2px',
                      borderBottom: `1px solid ${color.line}`,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 700,
                          color: color.ink,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {s.userAgent || 'Unknown device'}
                      </span>
                      <span style={{ display: 'block', fontSize: 11.5, color: color.muted }}>
                        Last used {dateTime(s.lastSeenAt)}
                      </span>
                    </span>
                    {s.current && (
                      <span
                        style={{
                          fontSize: 10.5,
                          fontWeight: 800,
                          background: color.tint,
                          color: color.blueDeep,
                          padding: '3px 9px',
                          borderRadius: 999,
                          flex: 'none',
                        }}
                      >
                        THIS DEVICE
                      </span>
                    )}
                  </div>
                ))}
                {rows.length === 0 && (
                  <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>
                    No other sessions.
                  </p>
                )}
              </div>
            )}

            {signOutOthers.error && (
              <div style={{ marginTop: 12 }}>
                <ErrorNote message={(signOutOthers.error as Error).message} />
              </div>
            )}

            <Button
              variant="ghost"
              onClick={() => signOutOthers.mutate()}
              disabled={signOutOthers.isPending || rows.filter((s) => !s.current).length === 0}
              style={{ marginTop: 14, border: `1.5px solid ${color.blue}`, color: color.blue }}
            >
              {signOutOthers.isPending ? 'Signing out…' : 'Sign out everywhere else'}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
